import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { FC, memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { useConvenienceDispatchShiftTruckMutation } from 'src/hooks/useConvenienceDispatchShiftTruck.mutation';
import { usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';
import PlanningTemporaryTruckInlineForm, { PlanningTemporaryTruckInlineFormHandle } from 'src/pages/V3Plans/presenters/PlanningTemporaryTruckInlineForm';

type VehicleMode = 'normal' | 'temporary';

type NormalEntry = {
  id: string;
  mode: 'normal';
  truck: PlanningsTruckEntity | null;
  driver: PlanningsDriverEntity | null;
  startAt: string;
  endAt: string;
  laborHours: number | '';
  /** truck が補完処理で自動設定された値か */
  truckAutoFilled: boolean;
  /** driver が補完処理で自動設定された値か */
  driverAutoFilled: boolean;
};

type TemporaryEntry = {
  id: string;
  mode: 'temporary';
};

type VehicleEntry = NormalEntry | TemporaryEntry;

const createNormalEntry = (startOn: string): NormalEntry => ({
  id: crypto.randomUUID(),
  mode: 'normal',
  truck: null,
  driver: null,
  startAt: `${startOn}T00:00`,
  endAt: `${startOn}T23:59`,
  laborHours: '',
  truckAutoFilled: false,
  driverAutoFilled: false,
});

const createTemporaryEntry = (): TemporaryEntry => ({
  id: crypto.randomUUID(),
  mode: 'temporary',
});

type Props = {
  open: boolean;
  onClose: () => void;
  startOn: string;
  endOn: string;
  existingTruckIds: Set<number>;
  existingDriverIds: Set<number>;
  /** Presenter で既に取得済みのマスタデータをそのまま利用して重複フェッチを避ける */
  trucks: PlanningsTruckEntity[];
  drivers: PlanningsDriverEntity[];
};

const truckOptionLabel = (truck: PlanningsTruckEntity): string =>
  truck.licensePlateValue || `truck-${truck.id}`;

type EntryErrors = {
  truck?: string;
  driver?: string;
  /** API レスポンスで返ってきた当該 entry の登録失敗メッセージ。
   *  複数エントリのうち一部だけ失敗したケースで残ったエントリに紐付けて表示する。 */
  submit?: string;
};

type NormalEntryCardProps = {
  entry: NormalEntry;
  index: number;
  total: number;
  startOn: string;
  endOn: string;
  trucks: PlanningsTruckEntity[];
  partnerCompanies: PartnerCompanyEntity[];
  partnerCompaniesLoading: boolean;
  drivers: PlanningsDriverEntity[];
  /** 同一ダイアログ内で他のエントリが選択済みの truck の id set */
  otherSelectedTruckIds: Set<number>;
  /** 同一ダイアログ内で他のエントリが選択済みの driver の id set */
  otherSelectedDriverIds: Set<number>;
  errors?: EntryErrors;
  onUpdate: (id: string, updates: Partial<NormalEntry>) => void;
  onModeChange: (id: string, mode: VehicleMode) => void;
  onRemove: (id: string) => void;
};

const NormalEntryCard: FC<NormalEntryCardProps> = memo(({
  entry, index, total, startOn, endOn,
  trucks,
  partnerCompanies, partnerCompaniesLoading,
  drivers,
  otherSelectedTruckIds,
  otherSelectedDriverIds,
  errors,
  onUpdate, onModeChange, onRemove,
}) => {
  const [partnerCompany, setPartnerCompany] = useState<PartnerCompanyEntity | null>(null);

  // 他エントリで選択中の truck/driver は除外（ただし自分が選んでいるものは表示する）
  const availableTrucks = useMemo(
    () => trucks.filter((t) => !otherSelectedTruckIds.has(t.id) || entry.truck?.id === t.id),
    [trucks, otherSelectedTruckIds, entry.truck]
  );
  const availableDrivers = useMemo(
    () => drivers.filter((d) => !otherSelectedDriverIds.has(d.id) || entry.driver?.id === d.id),
    [drivers, otherSelectedDriverIds, entry.driver]
  );

  // PlanningsTruckEntity は partnerCompanyId を持つ（取引先トラックの場合のみ set）
  const filteredTrucks = useMemo(
    () =>
      partnerCompany != null
        ? availableTrucks.filter((t) => t.partnerCompanyId === partnerCompany.id)
        : availableTrucks,
    [availableTrucks, partnerCompany]
  );

  const handlePartnerCompanyChange = useCallback((_: unknown, pc: PartnerCompanyEntity | null) => {
    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '車両追加 運送会社',
      label: pc?.name ?? '',
    });
    setPartnerCompany(pc);
    // partner 会社切替で現在の truck が合わなくなる場合はクリア
    if (pc && entry.truck && entry.truck.partnerCompanyId !== pc.id) {
      onUpdate(entry.id, { truck: null });
    }
  }, [entry.id, entry.truck, onUpdate]);

  const selectableHours = useMemo(() => {
    if (!entry.startAt || !entry.endAt) return [];
    const diffMs = new Date(entry.endAt).getTime() - new Date(entry.startAt).getTime();
    if (Number.isNaN(diffMs) || diffMs <= 0) return [];
    const hours = Math.min(Math.round(Math.abs(diffMs) / (3600 * 1000)), 23);
    return Array.from({ length: hours }, (_, i) => i + 1);
  }, [entry.startAt, entry.endAt]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Stack spacing={2}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight="bold">
            {total > 1 ? `${index + 1}台目` : '車両情報'}
          </Typography>
          {total > 1 && (
            <IconButton size="small" onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '車両追加 エントリ削除',
              });
              onRemove(entry.id);
            }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* 登録失敗時のサーバエラーメッセージ (このエントリ単位) */}
        {errors?.submit && (
          <Typography color="error" variant="body2">
            {errors.submit}
          </Typography>
        )}

        {/* モード切替 */}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            sx={{ fontSize: '0.75rem' }}
          >
            通常車両
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              ReactGA.event('select', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '車両追加 モード切替',
                label: 'temporary',
              });
              onModeChange(entry.id, 'temporary');
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            仮車両
          </Button>
        </Stack>

        {/* Row 1: 運送会社 (フィルタ) / 車両名 */}
        <Stack direction="row" spacing={1.5}>
          <Autocomplete
            options={partnerCompanies}
            getOptionLabel={(pc) => pc.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={partnerCompany}
            onChange={handlePartnerCompanyChange}
            loading={partnerCompaniesLoading}
            size="small"
            fullWidth
            renderOption={(props, pc) => <li {...props} key={pc.id}>{pc.name}</li>}
            renderInput={(params) => (
              <TextField {...params} label="運送会社" placeholder="絞り込み（任意）" />
            )}
          />
          <Autocomplete
            options={filteredTrucks}
            getOptionLabel={truckOptionLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={entry.truck}
            onChange={(_, truck) => {
              ReactGA.event('select', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '車両追加 車両名',
                label: truck ? truckOptionLabel(truck) : '',
              });
              // ユーザー自身が選んだ値として記録
              const updates: Partial<NormalEntry> = { truck, truckAutoFilled: false };
              // ドライバーが未設定 or 自動補完値の場合のみ上書き
              if (truck && (!entry.driver || entry.driverAutoFilled)) {
                const match = availableDrivers.find((d) => d.defaultTruckId === truck.id);
                if (match) {
                  updates.driver = match;
                  updates.driverAutoFilled = true;
                } else if (entry.driverAutoFilled) {
                  // 以前の自動補完ドライバーが新しい車両と合わなくなったらクリア
                  updates.driver = null;
                  updates.driverAutoFilled = false;
                }
              }
              onUpdate(entry.id, updates);
            }}
            size="small"
            fullWidth
            renderOption={(props, truck) => <li {...props} key={truck.id}>{truckOptionLabel(truck)}</li>}
            renderInput={(params) => (
              <TextField
                {...params}
                label="車両名"
                placeholder="例：1号車"
                error={!!errors?.truck}
                helperText={errors?.truck}
              />
            )}
          />
        </Stack>

        {/* Row 2: ドライバー */}
        <Stack direction="row" spacing={1.5}>
          <Autocomplete
            options={availableDrivers}
            getOptionLabel={(d) => d.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={entry.driver}
            onChange={(_, driver) => {
              ReactGA.event('select', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '車両追加 ドライバー',
                label: driver?.name ?? '',
              });
              // ユーザー自身が選んだ値として記録
              const updates: Partial<NormalEntry> = { driver, driverAutoFilled: false };
              // 車両が未設定 or 自動補完値の場合のみ上書き
              if (driver && (!entry.truck || entry.truckAutoFilled)) {
                if (driver.defaultTruckId != null) {
                  const match = availableTrucks.find((t) => t.id === driver.defaultTruckId);
                  if (match) {
                    updates.truck = match;
                    updates.truckAutoFilled = true;
                  } else if (entry.truckAutoFilled) {
                    updates.truck = null;
                    updates.truckAutoFilled = false;
                  }
                } else if (entry.truckAutoFilled) {
                  updates.truck = null;
                  updates.truckAutoFilled = false;
                }
              }
              onUpdate(entry.id, updates);
            }}
            size="small"
            fullWidth
            renderOption={(props, d) => <li {...props} key={d.id}>{d.name}</li>}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ドライバー（任意）"
                placeholder="例：佐藤 太郎"
                error={!!errors?.driver}
                helperText={errors?.driver ?? '未選択の場合は未割当で登録します'}
              />
            )}
          />
        </Stack>

        {/* Row 3: 開始時間 / 終了時間 / 労働時間 */}
        <Stack direction="row" spacing={1.5} alignItems="flex-end">
          <TextField
            label="開始時間"
            type="datetime-local"
            size="small"
            sx={{ flex: 1 }}
            value={entry.startAt}
            onChange={(e) => onUpdate(entry.id, { startAt: e.target.value, laborHours: '' })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: `${startOn}T00:00`, max: `${endOn}T23:59` }}
          />
          <TextField
            label="終了時間"
            type="datetime-local"
            size="small"
            sx={{ flex: 1 }}
            value={entry.endAt}
            onChange={(e) => onUpdate(entry.id, { endAt: e.target.value, laborHours: '' })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: entry.startAt || `${startOn}T00:00`, max: `${endOn}T23:59` }}
          />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>労働時間</InputLabel>
            <Select
              label="労働時間"
              value={entry.laborHours}
              onChange={(e) => {
                const next = e.target.value as number | '';
                ReactGA.event('select', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '車両追加 労働時間',
                  label: String(next),
                });
                onUpdate(entry.id, { laborHours: next });
              }}
            >
              <MenuItem value=""><em>未指定</em></MenuItem>
              {selectableHours.map((h) => (
                <MenuItem key={h} value={h}>{h}h</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </Box>
  );
});

NormalEntryCard.displayName = 'NormalEntryCard';

type TemporaryEntryCardProps = {
  entry: TemporaryEntry;
  index: number;
  total: number;
  startOn: string;
  endOn: string;
  errors?: EntryErrors;
  onModeChange: (id: string, mode: VehicleMode) => void;
  onRemove: (id: string) => void;
  onFormRef: (id: string, handle: PlanningTemporaryTruckInlineFormHandle | null) => void;
};

export const TemporaryEntryCard: FC<TemporaryEntryCardProps> = memo(({
  entry, index, total, startOn, endOn, errors, onModeChange, onRemove, onFormRef,
}) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50', p: 2 }}>
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" fontWeight="bold">
          {total > 1 ? `${index + 1}台目` : '車両情報'}
        </Typography>
        {total > 1 && (
          <IconButton size="small" onClick={() => {
            ReactGA.event('click', {
              screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
              button_name: '車両追加 エントリ削除',
            });
            onRemove(entry.id);
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {errors?.submit && (
        <Typography color="error" variant="body2">
          {errors.submit}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            ReactGA.event('select', {
              screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
              button_name: '車両追加 モード切替',
              label: 'normal',
            });
            onModeChange(entry.id, 'normal');
          }}
          sx={{ fontSize: '0.75rem' }}
        >
          通常車両
        </Button>
        <Button
          size="small"
          variant="contained"
          sx={{ fontSize: '0.75rem' }}
        >
          仮車両
        </Button>
      </Stack>
      <PlanningTemporaryTruckInlineForm
        ref={(handle) => onFormRef(entry.id, handle)}
        hideActions
        startOn={startOn}
        endOn={endOn}
        onSaved={() => onRemove(entry.id)}
        onCancel={() => onModeChange(entry.id, 'normal')}
      />
    </Stack>
  </Box>
));

TemporaryEntryCard.displayName = 'TemporaryEntryCard';

const ConvenienceDispatchAddVehicleDialog: FC<Props> = memo(({
  open, onClose, startOn, endOn, existingTruckIds, existingDriverIds, trucks: truckSource, drivers: driverSource,
}) => {
  const [entries, setEntries] = useState<VehicleEntry[]>(() => [createNormalEntry(startOn)]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, EntryErrors>>({});
  const tempFormRefs = useRef(new Map<string, PlanningTemporaryTruckInlineFormHandle>());
  // 二重発火ガード: LoadingButton の loading 反映 (= setIsSubmitting(true) の re-render) より
  // 早く onClick が連続実行されると 2 回 POST されてしまうため、ref で同期チェックする。
  const submittingRef = useRef(false);

  const handleTempFormRef = useCallback((id: string, handle: PlanningTemporaryTruckInlineFormHandle | null) => {
    if (handle) {
      tempFormRefs.current.set(id, handle);
    } else {
      tempFormRefs.current.delete(id);
    }
  }, []);

  const normalEntries = entries.filter((e): e is NormalEntry => e.mode === 'normal');

  // POST /api/v5/convenience_dispatches/shift_trucks は `selected_company_id` で絞り込むため、
  // driver 側は companyId 列があるので現在会社で絞り込む。
  // truck 側は PlanningsTruckEntity に companyId が無いが、
  // /api/v3/plannings/.../trucks は当該 planning のために適切にスコープ済みである前提。
  // 取引先トラック（partnerCompanyId が set）もそのまま候補に含め、必要に応じて
  // 「運送会社」フィルター (partnerCompany Autocomplete) で絞り込ませる。
  const licenseContext = useContext(LicenseContext);
  const selectedCompanyId = licenseContext.config?.selected_company_id ?? null;

  // 仮トラック・仮ドライバー(temporary) は除外（「仮車両」モード側で扱うため選択肢には含めない）
  const trucks = useMemo(
    () => truckSource.filter(
      (t) => !existingTruckIds.has(t.id) && !t.temporary,
    ),
    [truckSource, existingTruckIds]
  );

  const partnerCompaniesQuery = usePartnerCompaniesRequest(0, '', '', open, Number.MAX_SAFE_INTEGER);
  const partnerCompanies = partnerCompaniesQuery.data?.data ?? [];

  const drivers = useMemo(
    () => driverSource.filter(
      (d) => !existingDriverIds.has(d.id)
        && (selectedCompanyId == null || d.companyId === selectedCompanyId)
        && !d.temporary,
    ),
    [driverSource, existingDriverIds, selectedCompanyId]
  );

  // ダイアログ内の全 normal entry が現時点で選択中の truck / driver の id セット
  const selectedTruckIds = useMemo(
    () => new Set(normalEntries.map((e) => e.truck?.id).filter((id): id is number => typeof id === 'number')),
    [normalEntries]
  );
  const selectedDriverIds = useMemo(
    () => new Set(normalEntries.map((e) => e.driver?.id).filter((id): id is number => typeof id === 'number')),
    [normalEntries]
  );

  const { create } = useConvenienceDispatchShiftTruckMutation();

  const handleUpdate = useCallback((id: string, updates: Partial<NormalEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...updates } as VehicleEntry : e)));
    if ('truck' in updates || 'driver' in updates) {
      setValidationErrors((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev[id] };
        if ('truck' in updates) delete next.truck;
        if ('driver' in updates) delete next.driver;
        if (Object.keys(next).length === 0) {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: next };
      });
    }
  }, []);

  const handleModeChange = useCallback((id: string, mode: VehicleMode) => {
    setEntries((prev) => prev.map((e) => {
      if (e.id !== id) return e;
      return mode === 'normal' ? createNormalEntry(startOn) : createTemporaryEntry();
    }));
  }, [startOn]);

  const handleRemove = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    // entry を消したら、その id 紐付けの validation / submit エラーも捨てる
    setValidationErrors((prev) => {
      if (!prev[id]) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleAddEntry = useCallback(() => {
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '車両追加 もう1台追加',
    });
    setEntries((prev) => {
      const newEntry = createNormalEntry(startOn);
      // 直前の normal entry の開始/終了時間・労働時間を引き継ぐ
      const lastNormal = [...prev].reverse().find((e): e is NormalEntry => e.mode === 'normal');
      if (lastNormal) {
        newEntry.startAt = lastNormal.startAt;
        newEntry.endAt = lastNormal.endAt;
        newEntry.laborHours = lastNormal.laborHours;
      }
      return [...prev, newEntry];
    });
  }, [startOn]);

  const handleClose = useCallback(() => {
    ReactGA.event('close', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '車両追加',
    });
    setEntries([createNormalEntry(startOn)]);
    setErrorMessage(null);
    setValidationErrors({});
    onClose();
  }, [onClose, startOn]);

  const handleSubmit = useCallback(async () => {
    // 連打 / re-render 前の二重 click による多重 POST 防止 (state は閉包が古いので ref で判定)
    if (submittingRef.current) return;

    const validationErrorsByEntry: Record<string, EntryErrors> = {};
    for (const entry of normalEntries) {
      const e: EntryErrors = {};
      if (!entry.truck) e.truck = '車両名を選択してください';
      // ドライバー未選択は「未割当」として許可する（必須にしない）
      if (Object.keys(e).length > 0) validationErrorsByEntry[entry.id] = e;
    }
    if (Object.keys(validationErrorsByEntry).length > 0) {
      setValidationErrors(validationErrorsByEntry);
      return;
    }
    // バリデーション通過後に apply イベントを送信する。
    // バリデーションエラーで止まったケースを「保存確定」として計測しないため。
    ReactGA.event('apply', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '車両追加 保存',
    });
    setValidationErrors({});
    setErrorMessage(null);
    submittingRef.current = true;
    setIsSubmitting(true);

    // 各 entry の登録結果を個別に追跡する。一部失敗しても成功した entry は state から除き、
    // 失敗した entry にはサーバエラーメッセージを紐付けて残す (= 二重登録を回避し、
    // 残ったエントリだけリトライできるようにする)。
    const succeededNormalIds: string[] = [];
    const submitErrorById = new Map<string, string>();
    const extractApiError = (error: unknown): string => {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      return axiosError.response?.data?.error ?? axiosError.response?.data?.message ?? '登録に失敗しました。';
    };

    try {
      for (const entry of normalEntries) {
        try {
          await create.mutateAsync({
            truck_id: entry.truck.id,
            start_at: entry.startAt,
            end_at: entry.endAt,
            driver_id: entry.driver?.id,
            labor_hours: typeof entry.laborHours === 'number' ? entry.laborHours : undefined,
          });
          succeededNormalIds.push(entry.id);
        } catch (error) {
          submitErrorById.set(entry.id, extractApiError(error));
        }
      }

      // ループ中に Map が delete + 再 set される (ref callback の再発火) と Map iterator が
      // 再訪問してしまうので、ループ開始時点の handle 一覧を [id, handle] で固定し、
      // 同一 id の二重 submit を起こさないように Set でガードする。
      // 仮車両は inline form 側 onSaved → onRemove で成功時に entries から消える。
      const tempEntries: Array<[string, PlanningTemporaryTruckInlineFormHandle]> = Array.from(tempFormRefs.current.entries());
      const submittedIds = new Set<string>();
      for (const [id, formHandle] of tempEntries) {
        if (submittedIds.has(id)) continue;
        submittedIds.add(id);
        try {
          await formHandle.submit();
        } catch (error) {
          submitErrorById.set(id, extractApiError(error));
        }
      }

      // 成功した通常車両 entry を state から除く (= 残ったエラー行だけ表示される)
      if (succeededNormalIds.length > 0) {
        setEntries((prev) => prev.filter((e) => !succeededNormalIds.includes(e.id)));
      }

      if (submitErrorById.size === 0) {
        // 全件成功 → ダイアログを閉じてリセット
        handleClose();
      } else {
        // 一部失敗 → 各 entry にサーバエラーを紐付けて表示。共通の errorMessage は出さない。
        setValidationErrors(Object.fromEntries(
          Array.from(submitErrorById.entries()).map(([id, msg]) => [id, { submit: msg }]),
        ));
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [create, handleClose, normalEntries]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>車両追加</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          配車ボードに車両を追加します。複数台の一括登録も可能です。
        </DialogContentText>

        <Stack spacing={2}>
          {entries.map((entry, index) =>
            entry.mode === 'normal' ? (
              <NormalEntryCard
                key={entry.id}
                entry={entry}
                index={index}
                total={entries.length}
                startOn={startOn}
                endOn={endOn}
                trucks={trucks}
                partnerCompanies={partnerCompanies}
                partnerCompaniesLoading={partnerCompaniesQuery.isLoading}
                drivers={drivers}
                otherSelectedTruckIds={selectedTruckIds}
                otherSelectedDriverIds={selectedDriverIds}
                errors={validationErrors[entry.id]}
                onUpdate={handleUpdate}
                onModeChange={handleModeChange}
                onRemove={handleRemove}
              />
            ) : (
              <TemporaryEntryCard
                key={entry.id}
                entry={entry}
                index={index}
                total={entries.length}
                startOn={startOn}
                endOn={endOn}
                errors={validationErrors[entry.id]}
                onModeChange={handleModeChange}
                onRemove={handleRemove}
                onFormRef={handleTempFormRef}
              />
            )
          )}
        </Stack>

        {/* 仮車両モードの場合は、そのフォーム内で複数登録できるため「もう1台追加」は非表示 */}
        {entries[entries.length - 1]?.mode !== 'temporary' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              size="small"
            >
              もう1台追加
            </Button>
          </>
        )}

        {errorMessage && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          キャンセル
        </Button>
        <LoadingButton
          variant="contained"
          onClick={() => { void handleSubmit(); }}
          loading={isSubmitting}
        >
          保存
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

ConvenienceDispatchAddVehicleDialog.displayName = 'ConvenienceDispatchAddVehicleDialog';

export default ConvenienceDispatchAddVehicleDialog;
